/**
 * When enveloping a third party app form, manages the customization and functionality of it.
 * @property {string} dataset.formType - [data-form-type] will set the type of the form.
 * @property {string} dataset.formSelector - [data-form-selector] will set the selector of the third party form.
 * @property {string} dataset.cssAddress - [data-css-address] will set the css file to be fetched to style the shadow DOM.
 * @example <form-manager data-form-type="type" data-form-selector="#selector" data-css="./form.css">[formContent]</form-manager>
 */
class FormManager extends HTMLElement {
  submissionDisabled = false;

  constructor() {
    super();
    this.formSelector = this.dataset.formSelector || '';
    this.formType = this.dataset.formType || '';
    this.cssAddress = this.dataset.cssAddress || '';
    this.init();
  }

  /**
   * Initializes the form manager by setting up the form and applying customizations.
   * @private
   */
  async init() {
    console.log('Initializing the form manager.');
    this.signupForm = await this.catchSignupForm();
    if (this.signupForm) {
      this.initElements();
      this.applyStyles();
      this.initSignupForm();
    }
  }

  /**
   * Observes the DOM to detect when the signup form is available.
   * @returns {Promise<HTMLElement>} The detected signup form element.
   * @private
   */
  catchSignupForm() {
    return new Promise((resolve, reject) => {
      const form = this.querySelector(this.formSelector);
      if (form) return resolve(form);

      const observer = new MutationObserver((e) => {
        const detectedForm = this.querySelector(this.formSelector);
        if (detectedForm) {
          clearTimeout(timeoutId);
          resolve(detectedForm);
          observer.disconnect();
        }
      });

      observer.observe(this, { subtree: true, childList: true });

      const timeoutDuration = 10000; // 10 seconds
      const timeoutId = setTimeout(() => {
        observer.disconnect();
        reject(new Error('Signup form not detected within the timeout period.'));
      }, timeoutDuration);
    });
  }

  /**
   * Initializes form elements by selecting them from the DOM.
   * @private
   */
  initElements() {
    this.rootEmbed = this.signupForm.shadowRoot.querySelector('#app-embed');
    this.form = this.signupForm.shadowRoot.querySelector('form');
    this.formInputs = {};
    this.form.querySelectorAll('input, textarea').forEach((element) => {
      this.formInputs[element.id.replace('custom#', '')] = {
        element,
        ...(element.closest('fieldset')?.querySelector('legend') && {
          legend: element.closest('fieldset').querySelector('legend'),
        }),
      };
    });
    this.formEmailCheckbox = this.form.querySelector('fieldset[name="custom#email_signup_checkbox"]');
    this.formSubmit = this.form.querySelector('button[type="submit"]');
  }

  /**
   * Applies custom styles to the signup form.
   * @private
   */
  async applyStyles() {
    const styleContent = await this.generateStyleContent();
    const style = document.createElement('style');
    style.innerHTML = styleContent;
    this.signupForm.shadowRoot.appendChild(style);

    const headerElem = this.rootEmbed.querySelector('h2');
    headerElem.className = '';
    headerElem.classList.add('form-block__heading');

    /* Disable the form submit by default. */
    this.formSubmit.disabled = true;
    this.signupForm.shadowRoot.getElementById('label-email').innerHTML = '*Email Address';
  }

  /**
   * Generates custom CSS to be injected into the form's shadow DOM.
   * @returns {string} The generated CSS content.
   * @private
   */
  async generateStyleContent() {
    let styleContent = '';

    try {
      const response = await fetch(this.cssAddress);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const cssContent = await response.text();
      styleContent = cssContent;
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }

    return styleContent;
  }

  /**
   * Initializes the signup form, attaching event listeners and setting up validation.
   * @private
   */
  initSignupForm() {
    this.handleFormValidationOnChange();
    this.handleFormSubmit();
  }

  /**
   * Helper function that verifies if an email is invalid, checking if it is empty or it doesn't match valid format.
   * @private
   */
  isInvalidEmail = (email) => {
    const isEmpty = email.trim() === '';
    const regex =
      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    const isValid = regex.test(email);
    return isEmpty || !isValid;
  };

  /**
   * Handles form validation on change events
   * @private
   */
  handleFormValidationOnChange() {
    /* Add event listener to detect changes to the form and toggle the
     * state of the submit button when required field values are present. */
    this.form.addEventListener('change', () => {
      const validations = [this.isInvalidEmail(this.formInputs.email.element.value), this.submissionDisabled];

      this.formSubmit.disabled = validations.some(Boolean);
    });
  }

  /**
   * Handles form submission events
   * @private
   * @returns {Promise<void>}
   **/
  handleFormSubmit() {
    /* Add event listener to handle form submit. */
    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      console.log('Running the form submit listener.');
    });
  }
}

/*
 * FormManagerEMWA class extends FormManager to handle specific form Email Me When Available.
 * @extends FormManager
 */
class FormManagerEMWA extends FormManager {
  constructor() {
    super();
  }

  async initElements() {
    super.initElements();
    this.variantSKUInput = this.form.querySelector('input[id="custom#variant_sku"]');
    this.variantSKUInput.value = this.dataset.variantSku;
    this.variantSKUInput.dispatchEvent(new Event('input'));
    this.variantSKUInput.setAttribute('type', 'hidden');
    this.submitButton = this.form.querySelector('button[type="submit"]');
  }
  async applyStyles() {
    await super.applyStyles();
    this.form.classList.add('form-block__form');
    this.submitButton.classList.add('form-block__submit-button');
    this.variantSKUInput.parentElement.style.display = 'none';
  }
}
customElements.define('form-manager-emwa', FormManagerEMWA);

class FormManagerNotify extends FormManager {
  constructor() {
    super();
  }

  async initElements() {
    super.initElements();
    this.variantSKUInput = this.form.querySelector('input[id="custom#variant_sku"]');
    this.variantSKUInput.value = this.dataset.variantSku;
    this.variantSKUInput.dispatchEvent(new Event('input'));
    this.variantSKUInput.setAttribute('type', 'hidden');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.fieldsetAll = this.form.querySelectorAll('fieldset');
    this.fieldsetAll.forEach((fieldset, i) => {
      if (i < 2) fieldset.style.paddingLeft = '0';
      if (i > 1) {
        fieldset.style.display = 'none';

        const element = fieldset.querySelector('div label span');
        element.innerHTML = element.innerHTML.replace('* (Required)', '<strong>* (Required)</strong>');
        element.innerHTML = element.innerHTML.replace('(Optional)', '<strong>(Optional)</strong>');
        element.style.fontWeight = 'normal';
        element.style.fontSize = '15px';
      }
    });

    setTimeout(() => {
      const emailForm = this.form.querySelector('.form-block__form div input#email');
      const submitButton = this.form.querySelector('.form-block__form button.form-block__submit-button');
      const checkboxNVVWA = this.form.querySelector('#custom\\#nvwa_residency-true');
      const checkboxHDCCT = this.form.querySelector('#custom\\#consumer_health_data_collection_consent-true');
      const checkboxHDSCT = this.form.querySelector('#custom\\#consumer_health_data_sharing_consent-true');
      const checkboxHDDMCT = this.form.querySelector('#custom\\#consumer_health_data_media_consent-true');
      const checkboxNewsletter = this.form.querySelector('#emailConsent-true');

      const isValidEmail = (email) => {
        if (typeof email !== 'string') return false;
        email = email.trim();
        // Basic, safe regex: ensures something@something.something and no spaces
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
      };

      const areBothConsentCheckboxesChecked = () => {
        return checkboxHDCCT.checked && checkboxHDSCT.checked;
      };

      const updateSubmitButton = () => {
        if (checkboxNVVWA.checked) {
          if (areBothConsentCheckboxesChecked()) {
            if (isValidEmail(emailForm?.value)) {
              submitButton?.removeAttribute('disabled');
            }
          } else {
            submitButton?.setAttribute('disabled', 'true');
          }
        } else {
          if (isValidEmail(emailForm?.value)) {
            submitButton?.removeAttribute('disabled');
          }
        }
      };

      checkboxNewsletter.addEventListener('change', (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (checkboxNewsletter.checked) {
          this.fieldsetAll[1].style.display = 'block';
        } else {
          this.fieldsetAll.forEach((fieldset, i) => {
            if (i > 0) {
              fieldset.style.display = 'none';
              const checkbox = fieldset.querySelector('input[type="checkbox"]');
              if (checkbox) {
                checkbox.removeAttribute('required');
                checkbox.removeAttribute('aria-required');
                checkbox.checked = false;

                if (isValidEmail(emailForm?.value)) {
                  submitButton?.removeAttribute('disabled');
                }
              }
            }
          });
        }
      });

      checkboxNVVWA.addEventListener('change', (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (checkboxNVVWA.checked) {
          this.fieldsetAll.forEach((fieldset, i) => {
            if (i > 1) {
              fieldset.style.display = 'block';
              const checkbox = fieldset.querySelector('input[type="checkbox"]');
              if (checkbox && i < 4) {
                submitButton.setAttribute('disabled', 'true');
                checkbox?.setAttribute('required', 'true');
                checkbox?.setAttribute('aria-required', 'true');
              }
            }
          });
        } else {
          this.fieldsetAll.forEach((fieldset, i) => {
            if (i > 1) {
              fieldset.style.display = 'none';
              const checkbox = fieldset.querySelector('input[type="checkbox"]');
              if (checkbox) {
                checkbox.removeAttribute('required');
                checkbox.removeAttribute('aria-required');
                checkbox.checked = false;

                if (isValidEmail(emailForm?.value)) {
                  submitButton?.removeAttribute('disabled');
                }
              }
            }
          });
        }
      });

      const consentCheckboxes = [checkboxHDCCT, checkboxHDSCT, checkboxHDDMCT];

      const handleConsentChange = (e) => {
        e.stopPropagation();
        e.preventDefault();
        updateSubmitButton();
      };

      consentCheckboxes.forEach((cb) => {
        if (cb) cb.addEventListener('change', handleConsentChange);
      });
    }, 700);
  }
  async applyStyles() {
    await super.applyStyles();
    this.form.classList.add('form-block__form');
    this.submitButton.classList.add('form-block__submit-button');
    this.variantSKUInput.parentElement.style.display = 'none';
  }
}
customElements.define('form-manager-notify', FormManagerNotify);
